import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, Image as ImageIcon, Loader2, ArrowLeft, Mic, Footprints, Scan, StopCircle, Play, MapPin, Crosshair, X, Search, CheckCircle2, Upload, Sparkles, Leaf } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import exifr from 'exifr';
import api from '../lib/api';
import FirstDiscoveryFlow from '../components/FirstDiscoveryFlow';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const MODES = [
  { key: 'photo', label: 'Photo', icon: Camera, active: true },
  { key: 'audio', label: 'Bird Song', icon: Mic, active: true, badge: 'beta' },
  { key: 'ar', label: 'AR', icon: Scan, active: true, badge: 'beta' },
  { key: 'tracks', label: 'Tracks', icon: Footprints, active: false },
];

export default function Discover() {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0); // 0=idle 1=uploading 2=analysing 3=verifying 4=done
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('photo');
  // 'camera' → live capture (browser is at the location right now)
  // 'gallery' → uploaded from library, may need manual GPS
  const [pickSource, setPickSource] = useState(null);
  const [gps, setGps] = useState(null);   // {lat, lng, source: 'exif'|'device'|'manual'}
  const nav = useNavigate();
  const { user } = useAuth();

  const pick = (fromCamera) => {
    setPickSource(fromCamera ? 'camera' : 'gallery');
    (fromCamera ? cameraRef : inputRef).current?.click();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f));
    setGps(null);
    // For gallery uploads, try EXIF first — many phones bake GPS into the file.
    if (pickSource === 'gallery') {
      try {
        const out = await exifr.gps(f);
        if (out && Number.isFinite(out.latitude) && Number.isFinite(out.longitude)) {
          setGps({ lat: out.latitude, lng: out.longitude, source: 'exif' });
        }
      } catch { /* silently — file may not have EXIF */ }
    }
  };

  const identify = async () => {
    if (!file) return;
    setLoading(true);
    setStep(1); // uploading
    // Advance visible step every 900ms so the UI feels alive even
    // while the LLM is thinking. Real network completion snaps to
    // step 4 (done) below.
    const stepTimers = [
      window.setTimeout(() => setStep((s) => (s < 2 ? 2 : s)), 900),   // analysing
      window.setTimeout(() => setStep((s) => (s < 3 ? 3 : s)), 2600),  // verifying
    ];
    const form = new FormData();
    form.append('file', file);
    // Priority: manual/EXIF gps → live device geolocation (only for camera captures).
    if (gps && Number.isFinite(gps.lat) && Number.isFinite(gps.lng)) {
      form.append('latitude', gps.lat);
      form.append('longitude', gps.lng);
    } else if (pickSource === 'camera' && navigator.geolocation) {
      await new Promise(res => {
        navigator.geolocation.getCurrentPosition(
          (pos) => { form.append('latitude', pos.coords.latitude); form.append('longitude', pos.coords.longitude); res(); },
          () => res(),
          { timeout: 4000 }
        );
      });
    }
    try {
      const { data } = await api.post('/discover', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!data.success) {
        const msg = data.ai?.error
          ? `AI unavailable: ${data.ai.error.slice(0, 80)}. Please try again in a moment.`
          : (data.message || 'We could not identify wildlife in this photo. Try a clearer shot of the subject.');
        toast.error(msg);
      }
      else setResult(data);
      // Broadcast credits changed so header chip + reminder banner refresh
      try {
        const { announceCreditsChanged } = await import('../components/CreditsIndicator');
        announceCreditsChanged();
      } catch { /* non-fatal */ }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === 'object' ? detail?.code : null;
      if (code === 'no_discovery_credits' || e.response?.status === 402) {
        toast.error(
          (typeof detail === 'object' ? detail?.message : detail) ||
          'You are out of Discovery Credits. Upgrade to keep identifying wildlife.',
          {
            duration: 6500,
            action: { label: 'Upgrade', onClick: () => nav('/membership') },
          },
        );
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Identification failed'));
      }
    } finally {
      stepTimers.forEach(window.clearTimeout);
      setStep(4);
      // Give the "Done" tick a beat of screen time before the result renders.
      window.setTimeout(() => { setLoading(false); setStep(0); }, 350);
    }
  };

  const handleModeChange = (m) => {
    if (!m.active) { toast('Coming soon — this modality is on our roadmap.'); return; }
    if (m.key === 'ar') { nav('/ar'); return; }
    setMode(m.key);
    setPreview(null);
    setFile(null);
  };

  return (
    <div className="max-w-md mx-auto min-h-screen relative pb-10">
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="flex items-center gap-3 px-5 py-4">
          <button data-testid="back-btn" onClick={() => nav('/dashboard')} className="p-2 rounded-full hover:bg-white/5"><ArrowLeft className="w-5 h-5" /></button>
          <div><p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Discover</p><h1 className="font-serif text-xl leading-none mt-1">New Species</h1></div>
        </div>
      </header>

      <div className="px-5 pt-6 space-y-5">
        <div className="flex gap-2 overflow-x-auto -mx-5 px-5" data-testid="mode-selector">
          {MODES.map(m => (
            <button
              key={m.key}
              data-testid={`mode-${m.key}`}
              onClick={() => handleModeChange(m)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs whitespace-nowrap border transition-colors ${mode === m.key ? 'bg-accent text-black border-accent font-medium' : 'bg-card border-white/5 text-muted-foreground'}`}
            >
              <m.icon className="w-3.5 h-3.5" />{m.label}
              {m.badge && <span className="text-[8px] uppercase tracking-widest ml-1 opacity-70">{m.badge}</span>}
              {!m.active && <span className="text-[8px] uppercase tracking-widest ml-1 opacity-60">soon</span>}
            </button>
          ))}
        </div>

        {mode === 'photo' && (
          <PhotoMode
            preview={preview} loading={loading} step={step} onFile={onFile} pick={pick}
            cameraRef={cameraRef} inputRef={inputRef} identify={identify}
            pickSource={pickSource} gps={gps} setGps={setGps}
          />
        )}

        {mode === 'audio' && (
          <AudioMode
            onResult={setResult}
          />
        )}
      </div>

      {result && (
        <FirstDiscoveryFlow
          result={result}
          currentUser={user}
          onClose={() => { setResult(null); nav('/dashboard'); }}
        />
      )}
    </div>
  );
}

function IdentifyProgress({ step }) {
  const steps = [
    { key: 'upload',   label: 'Uploading photo',    Icon: Upload },
    { key: 'analyse',  label: 'Analysing wildlife', Icon: Sparkles },
    { key: 'verify',   label: 'Verifying identity', Icon: Leaf },
    { key: 'done',     label: 'Done',               Icon: CheckCircle2 },
  ];
  return (
    <div className="absolute inset-0 bg-[#040A08]/90 flex flex-col items-center justify-center gap-5 px-6" data-testid="identify-progress">
      <div className="flex flex-col gap-2.5 w-full max-w-xs" data-testid="identify-progress-steps">
        {steps.map((s, i) => {
          const active = step === i + 1;
          const done = step > i + 1;
          const state = done ? 'done' : active ? 'active' : 'pending';
          return (
            <div key={s.key} data-testid={`identify-step-${s.key}`} className={`flex items-center gap-3 transition-all ${state === 'pending' ? 'opacity-35' : 'opacity-100'}`}>
              <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center border ${state === 'done' ? 'bg-emerald-500/25 border-emerald-400/60 text-emerald-300' : state === 'active' ? 'bg-accent/25 border-accent/70 text-accent' : 'bg-white/5 border-white/10 text-muted-foreground'}`}>
                {state === 'done'
                  ? <CheckCircle2 className="w-4 h-4" strokeWidth={2.4} />
                  : state === 'active'
                    ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.4} />
                    : <s.Icon className="w-4 h-4" strokeWidth={2} />}
              </span>
              <span className={`text-sm ${state === 'done' ? 'text-emerald-200' : state === 'active' ? 'text-white font-medium' : 'text-white/70'}`}>{s.label}</span>
              {state === 'active' && (
                <span className="ml-auto text-[9px] uppercase tracking-widest text-accent">Now</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function PhotoMode({ preview, loading, step, onFile, pick, cameraRef, inputRef, identify, pickSource, gps, setGps }) {
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="wq-discover-hero rounded-3xl bg-card border border-white/5 aspect-square overflow-hidden relative">
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-center p-8">
            <div className="wq-discover-hero-icon w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mb-4">
              <Camera className="w-10 h-10 text-primary-light" strokeWidth={1.5} />
            </div>
            <p className="font-serif text-2xl">Capture Wildlife</p>
            <p className="wq-discover-hero-copy text-sm text-muted-foreground mt-2 max-w-xs">Take a clear photo of any bird, mammal, plant, insect, mushroom or reptile you spot.</p>
          </div>
        )}
        {loading && (
          <IdentifyProgress step={step} />
        )}
      </motion.div>

      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="camera-input" />
      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="gallery-input" />

      <div className="grid grid-cols-2 gap-3">
        <button data-testid="btn-camera" onClick={() => pick(true)} className="py-4 rounded-full bg-primary hover:bg-primary-light transition-colors flex items-center justify-center gap-2">
          <Camera className="w-4 h-4" /> Camera
        </button>
        <button data-testid="btn-gallery" onClick={() => pick(false)} className="py-4 rounded-full glass hover:bg-white/5 transition-colors flex items-center justify-center gap-2">
          <ImageIcon className="w-4 h-4" /> Gallery
        </button>
      </div>

      {/* GPS panel — appears for gallery uploads so the user can attach or
          correct coordinates when the photo has no EXIF location data. */}
      {preview && pickSource === 'gallery' && !loading && (
        <GpsPanel gps={gps} setGps={setGps} />
      )}

      {preview && !loading && (
        <button data-testid="btn-identify" onClick={identify} className="w-full py-4 rounded-full bg-accent text-black font-semibold hover:brightness-110 transition-all">
          Identify Species
        </button>
      )}

      <div className="pt-4 space-y-2 text-xs text-muted-foreground">
        <p>· Best results with clear, well-lit subjects</p>
        <p>· Location data adds a GPS bonus to your Discovery Score</p>
        <p>· Duplicate photos are automatically rejected</p>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------
// GPS panel — shown after a gallery upload. Displays the auto-detected
// EXIF location if present, or lets the user paste coordinates / capture
// their current device location manually.
// -----------------------------------------------------------------------
function GpsPanel({ gps, setGps }) {
  const [editing, setEditing] = useState(false);
  const [latStr, setLatStr] = useState(gps ? String(gps.lat) : '');
  const [lngStr, setLngStr] = useState(gps ? String(gps.lng) : '');
  const [locating, setLocating] = useState(false);
  const [addressStr, setAddressStr] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [resolvedLabel, setResolvedLabel] = useState('');

  // Geocode a free-form address / place name using OpenStreetMap Nominatim.
  // Nominatim is free and needs no key; we identify ourselves via a
  // descriptive User-Agent-style referer per their usage policy.
  const lookupAddress = async () => {
    const q = addressStr.trim();
    if (!q) {
      toast.error('Type a place, city or address first.');
      return;
    }
    setGeocoding(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&addressdetails=0&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error(`Lookup failed (${res.status})`);
      const rows = await res.json();
      if (!rows || rows.length === 0) {
        toast.error(`No match for "${q}". Try a nearby city or full address.`);
        setGeocoding(false);
        return;
      }
      const hit = rows[0];
      const lat = parseFloat(hit.lat);
      const lng = parseFloat(hit.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('Bad coordinates');
      setLatStr(String(lat));
      setLngStr(String(lng));
      setResolvedLabel(hit.display_name || q);
      setGps({ lat, lng, source: 'address', label: hit.display_name || q });
      toast.success('Location resolved from address.');
    } catch (e) {
      toast.error(e.message || 'Address lookup failed. Try coordinates instead.');
    } finally {
      setGeocoding(false);
    }
  };

  useEffect(() => {
    setLatStr(gps ? String(gps.lat) : '');
    setLngStr(gps ? String(gps.lng) : '');
  }, [gps]);

  const useDevice = () => {
    if (!navigator.geolocation) {
      toast.error('This device does not expose a GPS location.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, source: 'device' });
        setLocating(false);
        setEditing(false);
      },
      (err) => {
        toast.error(err.message || 'Could not read device location. Enter coordinates manually.');
        setLocating(false);
        setEditing(true);
      },
      { timeout: 8000, enableHighAccuracy: true },
    );
  };

  const saveManual = () => {
    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      toast.error('Latitude must be a number between −90 and 90.');
      return;
    }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      toast.error('Longitude must be a number between −180 and 180.');
      return;
    }
    setGps({ lat, lng, source: 'manual' });
    setEditing(false);
  };

  const clear = () => {
    setGps(null);
    setEditing(false);
    setLatStr('');
    setLngStr('');
  };

  const sourceLabel = gps?.source === 'exif' ? 'from photo'
    : gps?.source === 'device' ? 'from your device'
    : gps?.source === 'manual' ? 'entered manually'
    : gps?.source === 'address' ? 'from address'
    : null;

  return (
    <div className="rounded-2xl bg-card/80 border border-white/10 p-3" data-testid="gps-panel">
      <div className="flex items-center gap-2">
        <MapPin className={`w-4 h-4 ${gps ? 'text-emerald-300' : 'text-muted-foreground'}`} strokeWidth={2} />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Location</p>
          {gps ? (
            <p className="text-xs mt-0.5 font-mono tabular-nums truncate" data-testid="gps-panel-current">
              {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}
              <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-300/80">{sourceLabel}</span>
            </p>
          ) : (
            <p className="text-xs mt-0.5 text-muted-foreground" data-testid="gps-panel-empty">
              No GPS in this photo — add coordinates for the Discovery Score bonus.
            </p>
          )}
        </div>
        {gps && !editing && (
          <button
            onClick={clear}
            data-testid="gps-panel-clear"
            className="p-1 rounded-full hover:bg-white/10"
            aria-label="Clear location"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {!editing ? (
        <div className="flex gap-2 mt-2">
          <button
            onClick={useDevice}
            disabled={locating}
            data-testid="gps-panel-use-device"
            className="flex-1 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 disabled:opacity-60"
          >
            {locating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crosshair className="w-3 h-3" />}
            Use current
          </button>
          <button
            onClick={() => setEditing(true)}
            data-testid="gps-panel-enter-manual"
            className="flex-1 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5"
          >
            <MapPin className="w-3 h-3" /> Enter manually
          </button>
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          {/* Address lookup — paste a place / city / full address and we
              geocode via OpenStreetMap Nominatim to fill lat/lng below. */}
          <div className="flex gap-2">
            <input
              value={addressStr}
              onChange={e => setAddressStr(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupAddress(); } }}
              placeholder="Address or place (e.g. Ottawa Ontario)"
              data-testid="gps-panel-input-address"
              className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-xs outline-none focus:border-accent/60"
            />
            <button
              type="button"
              onClick={lookupAddress}
              disabled={geocoding}
              data-testid="gps-panel-lookup-address"
              className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10 text-[10px] uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-60"
            >
              {geocoding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              Look up
            </button>
          </div>
          {resolvedLabel && (
            <p className="text-[10px] text-emerald-300/80 truncate" data-testid="gps-panel-address-resolved">
              ✓ {resolvedLabel}
            </p>
          )}
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground/60 pt-1">Or enter coordinates</p>
          <div className="grid grid-cols-2 gap-2">
            <input
              value={latStr}
              onChange={e => setLatStr(e.target.value)}
              placeholder="Latitude"
              inputMode="decimal"
              data-testid="gps-panel-input-lat"
              className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-xs font-mono tabular-nums outline-none focus:border-accent/60"
            />
            <input
              value={lngStr}
              onChange={e => setLngStr(e.target.value)}
              placeholder="Longitude"
              inputMode="decimal"
              data-testid="gps-panel-input-lng"
              className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-xs font-mono tabular-nums outline-none focus:border-accent/60"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(false)}
              className="flex-1 py-2 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-widest"
            >
              Cancel
            </button>
            <button
              onClick={saveManual}
              data-testid="gps-panel-save-manual"
              className="flex-1 py-2 rounded-full bg-accent text-black text-[10px] uppercase tracking-widest font-semibold"
            >
              Save location
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground/70">
            Decimal degrees · e.g. <span className="font-mono text-white/60">45.4215, −75.6972</span>
          </p>
        </div>
      )}
    </div>
  );
}

function AudioMode({ onResult }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioUrl = blob ? URL.createObjectURL(blob) : null;

  useEffect(() => () => {
    try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch { /* noop */ }
    clearInterval(timerRef.current);
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = e => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        setBlob(new Blob(chunksRef.current, { type: 'audio/webm' }));
        stream.getTracks().forEach(t => t.stop());
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch (e) {
      toast.error('Microphone access denied');
    }
  };

  const stop = () => {
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const submit = async () => {
    if (!blob) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', blob, 'clip.webm');
    if (navigator.geolocation) {
      await new Promise(res => {
        navigator.geolocation.getCurrentPosition(
          (pos) => { form.append('latitude', pos.coords.latitude); form.append('longitude', pos.coords.longitude); res(); },
          () => res(), { timeout: 4000 }
        );
      });
    }
    try {
      const { data } = await api.post('/discover/audio', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.success) onResult(data);
      else toast.error(data.message || 'Bird-song ID failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bird-song ID failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="audio-mode">
      <div className="rounded-3xl bg-card border border-white/5 aspect-square flex flex-col items-center justify-center p-8 relative overflow-hidden">
        <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 ${recording ? 'bg-red-500/20' : 'bg-primary/20'}`}>
          <Mic className={`w-12 h-12 ${recording ? 'text-red-400' : 'text-primary-light'}`} strokeWidth={1.5} />
        </div>
        {recording ? (
          <>
            <p className="font-serif text-3xl tabular-nums" data-testid="recording-timer">{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</p>
            <p className="text-xs text-muted-foreground mt-1">Recording bird song…</p>
            {/* Animated pulse */}
            <div className="flex gap-1 mt-4">
              {[0, 1, 2, 3, 4].map(i => (
                <span key={i} className="w-1 rounded-full bg-red-400/70" style={{
                  animation: `pulseWave 1s ease-in-out ${i * 0.12}s infinite`,
                  height: 20 + (i % 2 ? 8 : 0),
                }} />
              ))}
            </div>
            <style>{`@keyframes pulseWave { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1.4); } }`}</style>
          </>
        ) : blob ? (
          <>
            <p className="font-serif text-xl">Ready to identify</p>
            <p className="text-xs text-muted-foreground mt-1">{Math.max(1, seconds)}s clip captured</p>
            <audio src={audioUrl} controls className="mt-4 w-full max-w-xs" />
          </>
        ) : (
          <>
            <p className="font-serif text-xl">Record a bird song</p>
            <p className="text-sm text-muted-foreground mt-2 text-center max-w-xs">Hold your phone up, capture 5–15 seconds of clear bird call, then tap identify.</p>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {!recording ? (
          <button data-testid="btn-record" onClick={start} className="py-4 rounded-full bg-primary hover:bg-primary-light transition-colors flex items-center justify-center gap-2">
            <Play className="w-4 h-4" /> {blob ? 'Re-record' : 'Record'}
          </button>
        ) : (
          <button data-testid="btn-stop" onClick={stop} className="py-4 rounded-full bg-red-500/80 hover:bg-red-500 transition-colors flex items-center justify-center gap-2 text-white">
            <StopCircle className="w-4 h-4" /> Stop
          </button>
        )}
        <button
          data-testid="btn-identify-audio"
          onClick={submit}
          disabled={!blob || busy}
          className="py-4 rounded-full bg-accent text-black font-semibold hover:brightness-110 disabled:opacity-30 transition-all flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Identify'}
        </button>
      </div>

      <div className="pt-2 space-y-2 text-xs text-muted-foreground">
        <p>· Bird-song ID is in beta — expect approximate matches for MVP</p>
        <p>· Best clips: 5–15 seconds, minimal wind and background traffic</p>
        <p>· Audio and location are stored with your observation</p>
      </div>
    </div>
  );
}
